import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, User, DollarSign, Calendar, CreditCard,
  Tag, FileText, Edit, ArrowLeft, Pause, Play,
  XCircle, Clock, CheckCircle, AlertCircle, Wifi, Shield, Router, Plus, Activity
} from 'lucide-react';
import api from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Tab, TabList, TabPanel, Tabs } from '../../components/UI/Tabs';
import { usePermissions } from '../../hooks/usePermissions';
import CancelarContratoModal from '../../components/Contracts/CancelarContratoModal';

const ContratoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [contrato, setContrato] = useState(null);
  const [boletos, setBoletos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [fidelizacao, setFidelizacao] = useState(null);
  const [equipamentos, setEquipamentos] = useState([]);
  const [servicosAdicionais, setServicosAdicionais] = useState([]);
  const [servicosAdicionaisDisponiveis, setServicosAdicionaisDisponiveis] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [showFidelizacaoModal, setShowFidelizacaoModal] = useState(false);
  const [showQuebrarFidelizacaoModal, setShowQuebrarFidelizacaoModal] = useState(false);
  const [showVincularEquipamentoModal, setShowVincularEquipamentoModal] = useState(false);
  const [showDesvincularEquipamentoModal, setShowDesvincularEquipamentoModal] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [showAdicionarServicoModal, setShowAdicionarServicoModal] = useState(false);
  const [showRemoverServicoModal, setShowRemoverServicoModal] = useState(false);
  const [equipamentoSelecionado, setEquipamentoSelecionado] = useState(null);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchServicosAdicionais = async () => {
    try {
      const response = await api.get(`/contratos/${id}/servicos-adicionais`);
      setServicosAdicionais(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.log('Serviços adicionais não disponíveis:', error);
    }
  };

  const fetchServicosAdicionaisDisponiveis = async () => {
    try {
      const response = await api.get('/servicos-adicionais/ativos');
      setServicosAdicionaisDisponiveis(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.log('Erro ao carregar serviços disponíveis:', error);
    }
  };

  const fetchPlanos = async () => {
    try {
      const response = await api.get('/planos');
      const payload = response.data;
      const items = Array.isArray(payload) ? payload : (Array.isArray(payload.data) ? payload.data : []);
      setPlanos(items);
    } catch (error) {
      console.log('Erro ao carregar planos:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const contratoRes = await api.get(`/contratos/${id}`);
      const contratoData = contratoRes.data;

      setContrato(contratoData);
      
      setBoletos(Array.isArray(contratoData.boletos) ? contratoData.boletos : []);
      
  await fetchServicosAdicionais();
  await fetchPlanos();
      
      try {
        const fidRes = await api.get(`/contratos/${id}/fidelizacao`);
        const data = fidRes.data;

        let f = null;
        if (data) {
          if (data.temFidelizacao && data.fidelizacao) {
            f = data.fidelizacao;
          } else if (data.contratoId || data.id || data.tipoFidelizacao) {
            f = data;
          }
        }

        if (f) {
          const normalized = {
            ...f,
            multa: f.multa || f.multaRescisao || f.valorMultaCobrado || null,
            mesesFidelidade: f.mesesFidelidade || f.meses_fidelidade || null,
            desconto: f.descontoConcedido || f.desconto || 0,
            mesesRestantes: f.mesesRestantes != null ? f.mesesRestantes : (f.mesesFidelidade != null ? f.mesesFidelidade : null),
            dataInicio: f.dataInicio || f.data_inicio || f.dataInicioContrato || null,
            dataFim: f.dataFim || f.data_fim || f.dataFimContrato || null,
            status: f.status || (f.mesesRestantes === 0 ? 'inativa' : (f.status === 'ativa' ? 'ativo' : f.status))
          };

          setFidelizacao(normalized);
        }
      } catch (error) {
        console.log('Fidelização não disponível:', error);
      }
      
      try {
        const equipRes = await api.get(`/contratos/${id}/equipamentos`);
        setEquipamentos(Array.isArray(equipRes.data) ? equipRes.data : []);
      } catch (error) {
        console.log('Equipamentos não disponíveis:', error);
      }
      
      try {
        const logsRes = await api.get(`/logs/registro/cad_contratos/${id}`);
        setHistorico(Array.isArray(logsRes.data) ? logsRes.data : []);
      } catch (error) {
        console.log('Histórico não disponível:', error);
      }
    } catch (error) {
      toast.error('Erro ao carregar contrato');
      console.error(error);
      navigate('/contratos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (action) => {
    try {
      let endpoint = '';
      let successMessage = '';
      
      switch (action) {
        case 'suspender':
          endpoint = `/contratos/${id}/suspender`;
          successMessage = 'Contrato suspenso com sucesso';
          break;
        case 'reativar':
          endpoint = `/contratos/${id}/reativar`;
          successMessage = 'Contrato reativado com sucesso';
          break;
        case 'cancelar':
          setShowCancelarModal(true);
          return;
        default:
          return;
      }
      
      await api.patch(endpoint);
      toast.success(successMessage);
      fetchData();
    } catch (error) {
      toast.error('Erro ao alterar status do contrato');
    }
  };

  const handleCancelarContrato = async (payload) => {
    try {
      await api.post(`/contratos/${id}/cancelar`, payload);
      toast.success('Contrato cancelado com sucesso');
      setShowCancelarModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao cancelar contrato');
      console.error(error);
    }
  };

  const handleCriarFidelizacao = async (data) => {
    try {
      await api.post('/contratos/fidelizacao', {
        contratoId: parseInt(id),
        ...data
      });
      toast.success('Fidelização criada com sucesso!');
      setShowFidelizacaoModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar fidelização');
      console.error(error);
    }
  };

  const handleRefidelizar = async (data) => {
    try {
      await api.post(`/contratos/${id}/refidelizar`, data);
      toast.success('Contrato refidelizado com sucesso!');
      setShowFidelizacaoModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao refidelizar contrato');
      console.error(error);
    }
  };

  const handleQuebrarFidelizacao = async (motivo) => {
    try {
      const response = await api.post(`/contratos/${id}/quebrar-fidelizacao`, { motivo });
      toast.success(response.data.message);
      setShowQuebrarFidelizacaoModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao quebrar fidelização');
      console.error(error);
    }
  };

  const handleVincularEquipamento = async (data) => {
    try {
      await api.post(`/contratos/${id}/equipamentos`, {
        equipamentoId: Number(data.equipamentoId),
        tipoVinculo: data.tipoVinculo,
        dataInstalacao: data.dataInstalacao || null,
        observacoes: data.observacoes || ''
      });
      toast.success('Equipamento vinculado com sucesso!');
      setShowVincularEquipamentoModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao vincular equipamento');
      console.error(error);
    }
  };

  const handleDesvincularEquipamento = async (motivoDesvinculo) => {
    try {
      await api.delete(`/contratos/equipamentos/vinculos`, {
        data: {
          vinculoId: Number(equipamentoSelecionado.id),
          motivo: motivoDesvinculo
        }
      });
      toast.success('Equipamento desvinculado com sucesso!');
      setShowDesvincularEquipamentoModal(false);
      setEquipamentoSelecionado(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao desvincular equipamento');
      console.error(error);
    }
  };

  const handleAdicionarServico = async (servicoId) => {
    try {
      await api.post(`/contratos/${id}/servicos-adicionais`, {
        servicoAdicionalId: Number(servicoId)
      });
      toast.success('Serviço adicional vinculado com sucesso!');
      setShowAdicionarServicoModal(false);
      fetchServicosAdicionais();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao adicionar serviço');
      console.error(error);
    }
  };

  const handleRemoverServico = async (motivo) => {
    try {
      await api.delete(`/contratos/${id}/servicos-adicionais`, {
        data: {
          servicoAdicionalId: Number(servicoSelecionado.id),
          motivo: motivo || 'Removido pelo usuário'
        }
      });
      toast.success('Serviço adicional removido com sucesso!');
      setShowRemoverServicoModal(false);
      setServicoSelecionado(null);
      fetchServicosAdicionais();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover serviço');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'ativo': 'bg-green-100 text-green-800',
      'instalado': 'bg-green-100 text-green-800',
      'suspenso': 'bg-yellow-100 text-yellow-800',
      'cancelado': 'bg-red-100 text-red-800',
      'aguardando': 'bg-blue-100 text-blue-800',
      'pendente': 'bg-orange-100 text-orange-800',
      'pago': 'bg-green-100 text-green-800',
      'vencido': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'ativo': <CheckCircle className="w-5 h-5 text-green-600" />,
      'instalado': <CheckCircle className="w-5 h-5 text-green-600" />,
      'suspenso': <Pause className="w-5 h-5 text-yellow-600" />,
      'cancelado': <XCircle className="w-5 h-5 text-red-600" />,
      'aguardando': <Clock className="w-5 h-5 text-blue-600" />
    };
    return icons[status] || <AlertCircle className="w-5 h-5 text-gray-600" />;
  };

  const isEquipAtivo = (equip) => {
    if (!equip) return false;
    if (equip.ativo === true) return true;
    if (equip.equipamento?.ativo === true) return true;
    const status = String(equip.status || equip.equipamento?.status || '').toLowerCase();
    return ['ativo', 'instalado'].includes(status);
  };

  const getEquipVinculoDate = (equip) => {
    if (!equip) return null;
    const candidates = [
      equip.dataInstalacao,
      equip.createdAt,
      equip.equipamento?.dataInstalacao,
      equip.equipamento?.createdAt,
    ];
    for (const d of candidates) {
      if (d) return d;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!contrato) return null;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/contratos')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Contrato #{contrato.id}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {contrato.cliente?.nome}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(contrato.status)}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(contrato.status)}`}>
              {contrato.status}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2">
          {canEdit('contratos') && (
            <>
              {contrato.status === 'ativo' && (
                <button
                  onClick={() => handleStatusChange('suspender')}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                >
                  <Pause className="w-4 h-4" />
                  Suspender
                </button>
              )}
              
              {contrato.status === 'suspenso' && (
                <button
                  onClick={() => handleStatusChange('reativar')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Reativar
                </button>
              )}
              
              {contrato.status !== 'cancelado' && (
                <button
                  onClick={() => handleStatusChange('cancelar')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Cancelar
                </button>
              )}
              
              <button
                onClick={() => navigate(`/contratos/${id}/diagnostico-conexao`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Activity className="w-4 h-4" />
                Diagnóstico de Conexão
              </button>
              
              <button
                onClick={() => navigate(`/contratos/${id}/editar`)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Informações Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cliente */}
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Cliente
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Nome</p>
              <p className="font-medium">{contrato.cliente?.nome}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">CPF/CNPJ</p>
              <p className="font-medium">{contrato.cliente?.cpfCnpj}</p>
            </div>
            <button
              onClick={() => navigate(`/clientes/${contrato.clienteId}/detalhes`)}
              className="text-primary-600 hover:text-primary-800 text-sm"
            >
              Ver detalhes do cliente →
            </button>
          </div>
        </div>

        {/* Plano */}
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Wifi className="w-5 h-5" />
            Plano
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Nome</p>
              <p className="font-medium">{contrato.plano?.nomePlano}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Velocidade</p>
              <p className="font-medium">
                {contrato.plano?.limitDownload || 0}/{contrato.plano?.limitUpload || 0} MB
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tecnologia</p>
              <p className="font-medium capitalize">{contrato.plano?.tipoTecnologia || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Valores */}
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Valores
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Mensalidade</p>
              <p className="font-medium text-lg">
                R$ {parseFloat(contrato.valorMensalidade || 0).toFixed(2)}
              </p>
              {parseFloat(contrato.valorOriginal || 0) !== parseFloat(contrato.valorMensalidade || 0) && (
                <p className="text-sm text-gray-500 line-through">
                  R$ {parseFloat(contrato.valorOriginal || 0).toFixed(2)}
                </p>
              )}
            </div>
            {parseFloat(contrato.desconto || 0) > 0 && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Desconto</p>
                <p className="font-medium text-green-600">
                  R$ {parseFloat(contrato.desconto || 0).toFixed(2)} por {contrato.mesesDescontoRestante || 0} meses
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Vencimento</p>
              <p className="font-medium">Dia {contrato.diaVencimento || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Forma de Pagamento</p>
              <p className="font-medium capitalize">{contrato.formaPagamento || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fidelização */}
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Fidelização
          </h2>
          {!fidelizacao && contrato.status === 'ativo' && (
            <button
              onClick={() => setShowFidelizacaoModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Criar Fidelização
            </button>
          )}
          {fidelizacao && contrato.status === 'ativo' && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowFidelizacaoModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                Refidelizar
              </button>
              <button
                onClick={() => setShowQuebrarFidelizacaoModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                Quebrar Fidelização
              </button>
            </div>
          )}
        </div>

        {fidelizacao ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Status</p>
              <p className="font-semibold text-lg capitalize">{fidelizacao.status}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Meses de Fidelidade</p>
              <p className="font-semibold text-lg">{fidelizacao.mesesFidelidade} meses</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <p className="text-sm text-green-600 dark:text-green-400 mb-1">Meses Restantes</p>
              <p className="font-semibold text-lg">{fidelizacao.mesesRestantes} meses</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">Multa</p>
              <p className="font-semibold text-lg">R$ {fidelizacao.multa}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">Data de Início</p>
              <p className="font-medium">{format(new Date(fidelizacao.dataInicio), 'dd/MM/yyyy')}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">Data de Término</p>
              <p className="font-medium">{format(new Date(fidelizacao.dataFim), 'dd/MM/yyyy')}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Este contrato não possui fidelização ativa</p>
            {contrato.status === 'ativo' && (
              <p className="text-sm mt-2">Clique em "Criar Fidelização" para fidelizar o cliente</p>
            )}
          </div>
        )}
      </div>

      {/* Datas e Observações */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Período
          </h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Início</p>
                <p className="font-medium">
                  {contrato.dataInicio 
                    ? format(new Date(contrato.dataInicio), 'dd/MM/yyyy')
                    : '-'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Fim</p>
                <p className="font-medium">
                  {contrato.dataFim 
                    ? format(new Date(contrato.dataFim), 'dd/MM/yyyy')
                    : 'Indeterminado'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Observações
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {contrato.observacoes || 'Nenhuma observação'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm sticky top-0 z-10">
          <TabList>
            <Tab>Faturas ({boletos.length})</Tab>
            <Tab>Equipamentos ({equipamentos.length})</Tab>
            <Tab>Serviços Adicionais ({servicosAdicionais.length})</Tab>
            <Tab>Vouchers ({contrato.vouchers?.length || 0})</Tab>
            <Tab>Promoções ({contrato.promocoes?.length || 0})</Tab>
            <Tab>Histórico</Tab>
          </TabList>
        </div>

        {/* Tab Faturas */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            {boletos.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 text-sm font-medium">Nosso Número</th>
                      <th className="text-left p-2 text-sm font-medium">Valor</th>
                      <th className="text-left p-2 text-sm font-medium">Vencimento</th>
                      <th className="text-left p-2 text-sm font-medium">Status</th>
                      <th className="text-left p-2 text-sm font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boletos.map((boleto) => (
                      <tr key={boleto.id} className="border-b">
                        <td className="p-2 text-sm">{boleto.nossoNumero || '-'}</td>
                        <td className="p-2 text-sm">
                          R$ {parseFloat(boleto.valor || 0).toFixed(2)}
                        </td>
                        <td className="p-2 text-sm">
                          {boleto.dataVencimento 
                            ? format(new Date(boleto.dataVencimento), 'dd/MM/yyyy')
                            : '-'
                          }
                        </td>
                        <td className="p-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(boleto.status)}`}>
                            {boleto.status || 'pendente'}
                          </span>
                        </td>
                        <td className="p-2">
                          <button
                            onClick={() => navigate(`/boletos/${boleto.id}`)}
                            className="text-primary-600 hover:text-primary-800 text-sm"
                          >
                            Ver detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma fatura encontrada para este contrato</p>
              </div>
            )}
          </div>
        </TabPanel>

        {/* Tab Equipamentos */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-semibold text-lg">Equipamentos Vinculados</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {equipamentos.length > 0 
                    ? `${equipamentos.length} equipamento(s) vinculado(s)`
                    : 'Nenhum equipamento vinculado ao contrato'
                  }
                </p>
              </div>
              {canEdit('contratos') && contrato.status === 'ativo' && (
                <button
                  onClick={() => setShowVincularEquipamentoModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                  Vincular Equipamento
                </button>
              )}
            </div>

            {Array.isArray(equipamentos) && equipamentos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {equipamentos.map((equip) => (
                  <div key={equip.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <Router className="w-6 h-6 text-primary-500" />
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(equip.status)}`}>
                        {equip.status}
                      </span>
                    </div>
                    <p className="font-medium text-lg mb-2">{equip.equipamento?.modelo || 'N/A'}</p>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <p><span className="font-medium">MAC:</span> {equip.equipamento?.macAddress || '-'}</p>
                      {equip.equipamento?.ip && (
                        <p><span className="font-medium">IP:</span> {equip.equipamento?.ip}</p>
                      )}
                      <p>
                        <span className="font-medium">Vinculado em:</span>{' '}
                        {getEquipVinculoDate(equip) || '-'}
                      </p>
                    </div>
                    {equip.observacoes && (
                      <p className="text-sm text-gray-500 mt-2 italic">{equip.observacoes}</p>
                    )}
                    
                    {canEdit('contratos') && isEquipAtivo(equip) && (
                      <button
                        onClick={() => {
                          setEquipamentoSelecionado(equip);
                          setShowDesvincularEquipamentoModal(true);
                        }}
                        className="mt-3 w-full px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-sm font-medium transition-colors"
                      >
                        Desvincular
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <Router className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium mb-2">Nenhum equipamento vinculado</p>
                <p className="text-sm">Use o botão acima para vincular o primeiro equipamento a este contrato</p>
              </div>
            )}
          </div>
        </TabPanel>

        {/* Tab Serviços Adicionais */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-500" />
                  Serviços Adicionais
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {servicosAdicionais.length > 0 
                    ? `${servicosAdicionais.length} serviço(s) vinculado(s) ao contrato`
                    : 'Nenhum serviço adicional vinculado'
                  }
                </p>
              </div>
              {canEdit('contratos') && contrato.status === 'ativo' && (
                <button
                  onClick={() => {
                    fetchServicosAdicionaisDisponiveis();
                    setShowAdicionarServicoModal(true);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar Serviço
                </button>
              )}
            </div>

            {Array.isArray(servicosAdicionais) && servicosAdicionais.length > 0 ? (
              <div className="space-y-4">
                {/* Resumo Financeiro */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Valor Total dos Serviços</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Adicional à mensalidade do plano
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      + R$ {servicosAdicionais.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0).toFixed(2)}
                      <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mês</span>
                    </p>
                  </div>
                </div>

                {/* Grid de Serviços */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {servicosAdicionais.map((servico) => (
                    <div 
                      key={servico.id} 
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-all hover:border-purple-300 dark:hover:border-purple-700"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                            {servico.nome}
                          </h4>
                          {servico.categoria && (
                            <span className="inline-block text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                              {servico.categoria}
                            </span>
                          )}
                        </div>
                        <Package className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      </div>

                      {servico.descricao && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                          {servico.descricao}
                        </p>
                      )}

                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Valor Mensal:</span>
                          <span className="font-bold text-purple-600 dark:text-purple-400">
                            R$ {parseFloat(servico.valorMensal || 0).toFixed(2)}
                          </span>
                        </div>
                        
                        {servico.valorInstalacao && parseFloat(servico.valorInstalacao) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Instalação:</span>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              R$ {parseFloat(servico.valorInstalacao).toFixed(2)}
                            </span>
                          </div>
                        )}
                        
                        {servico.requerEquipamento && (
                          <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                            <Router className="w-3 h-3" />
                            <span>Requer equipamento</span>
                          </div>
                        )}
                      </div>

                      {servico.observacoes && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-3 border-t border-gray-200 dark:border-gray-700 pt-2">
                          {servico.observacoes}
                        </p>
                      )}

                      {canEdit('contratos') && contrato.status === 'ativo' && (
                        <button
                          onClick={() => {
                            setServicoSelecionado(servico);
                            setShowRemoverServicoModal(true);
                          }}
                          className="w-full px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Remover Serviço
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Informação sobre o valor total do contrato */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800 dark:text-blue-200">Valor Total do Contrato</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Plano: R$ {parseFloat(contrato.valorOriginal || 0).toFixed(2)} + 
                        Serviços: R$ {servicosAdicionais.reduce((total, s) => total + parseFloat(s.valorMensal || 0), 0).toFixed(2)} = 
                        <strong className="ml-1">R$ {parseFloat(contrato.valorMensalidade || 0).toFixed(2)}/mês</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium mb-2">Nenhum serviço adicional vinculado</p>
                <p className="text-sm mb-4">
                  Adicione serviços complementares para incrementar o valor do contrato
                </p>
                {canEdit('contratos') && contrato.status === 'ativo' && (
                  <button
                    onClick={() => {
                      fetchServicosAdicionaisDisponiveis();
                      setShowAdicionarServicoModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Plus className="w-5 h-5" />
                    Adicionar Primeiro Serviço
                  </button>
                )}
              </div>
            )}
          </div>
        </TabPanel>

        {/* Tab Vouchers */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            {contrato.vouchers?.length > 0 ? (
              <div className="space-y-4">
                {contrato.vouchers.map((voucher) => (
                  <div key={voucher.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{voucher.codigo}</p>
                        <p className="text-sm text-gray-600 mt-1">{voucher.descricao}</p>
                        <p className="text-sm text-gray-500 mt-2">
                          Tipo: {voucher.tipo} | Valor: {voucher.valor}
                          {voucher.tipo === 'percentual' ? '%' : ' reais'}
                        </p>
                      </div>
                      <Tag className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                Nenhum voucher aplicado
              </p>
            )}
          </div>
        </TabPanel>

        {/* Tab Promoções */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            {contrato.promocoes?.length > 0 ? (
              <div className="space-y-4">
                {contrato.promocoes.map((promocao) => (
                  <div key={promocao.id} className="border rounded-lg p-4">
                    <p className="font-medium">{promocao.nome}</p>
                    <p className="text-sm text-gray-600 mt-1">{promocao.descricao}</p>
                    <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-500">Tipo:</span>
                        <span className="ml-2">{promocao.tipo}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Valor:</span>
                        <span className="ml-2">
                          {promocao.valor}
                          {promocao.tipo.includes('percentual') ? '%' : ' reais'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Duração:</span>
                        <span className="ml-2">{promocao.mesesDuracao} meses</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">
                Nenhuma promoção aplicada
              </p>
            )}
          </div>
        </TabPanel>

        {/* Tab Histórico */}
        <TabPanel>
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
            {historico.length > 0 ? (
              <div className="space-y-4">
                {historico.map((log, index) => (
                  <div key={index} className="border-l-4 border-gray-200 pl-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{log.acao || 'Ação não especificada'}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Por: {log.usuario || 'Sistema'}
                        </p>
                      </div>
                      <p className="text-sm text-gray-500">
                        {log.timestamp 
                          ? format(new Date(log.timestamp), "dd/MM/yyyy 'às' HH:mm")
                          : '-'
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum histórico disponível para este contrato</p>
              </div>
            )}
          </div>
        </TabPanel>
      </Tabs>

      {/* Modals */}
      {showFidelizacaoModal && (
        <FidelizacaoModal
          fidelizacao={fidelizacao}
          planos={planos}
          onClose={() => setShowFidelizacaoModal(false)}
          onSubmit={fidelizacao ? handleRefidelizar : handleCriarFidelizacao}
        />
      )}

      {showQuebrarFidelizacaoModal && (
        <QuebrarFidelizacaoModal
          fidelizacao={fidelizacao}
          onClose={() => setShowQuebrarFidelizacaoModal(false)}
          onSubmit={handleQuebrarFidelizacao}
        />
      )}

      {showVincularEquipamentoModal && (
        <VincularEquipamentoModal
          onClose={() => setShowVincularEquipamentoModal(false)}
          onSubmit={handleVincularEquipamento}
        />
      )}

      {showDesvincularEquipamentoModal && (
        <DesvincularEquipamentoModal
          equipamento={equipamentoSelecionado}
          onClose={() => {
            setShowDesvincularEquipamentoModal(false);
            setEquipamentoSelecionado(null);
          }}
          onSubmit={handleDesvincularEquipamento}
        />
      )}

      {showAdicionarServicoModal && (
        <AdicionarServicoModal
          servicosDisponiveis={servicosAdicionaisDisponiveis.filter(
            s => !servicosAdicionais.find(sa => sa.id === s.id)
          )}
          onClose={() => setShowAdicionarServicoModal(false)}
          onSubmit={handleAdicionarServico}
        />
      )}

      {showRemoverServicoModal && (
        <RemoverServicoModal
          servico={servicoSelecionado}
          onClose={() => {
            setShowRemoverServicoModal(false);
            setServicoSelecionado(null);
          }}
          onSubmit={handleRemoverServico}
        />
      )}
      {showCancelarModal && (
        <CancelarContratoModal
          open={showCancelarModal}
          onClose={() => setShowCancelarModal(false)}
          onSubmit={handleCancelarContrato}
        />
      )}
    </div>
  );
};

// Componentes de Modais
const FidelizacaoModal = ({ fidelizacao, planos = [], onClose, onSubmit }) => {
  // Campos comuns
  const [tipoFidelizacao, setTipoFidelizacao] = useState(fidelizacao?.tipoFidelizacao || 'fidelidade_12_meses');
  const [mesesFidelidade, setMesesFidelidade] = useState(fidelizacao?.mesesFidelidade || 12);

  // Criar fidelização
  const [dataInicio, setDataInicio] = useState(fidelizacao?.dataInicio ? format(new Date(fidelizacao.dataInicio), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
  const [multaRescisao, setMultaRescisao] = useState(fidelizacao?.multa || 0);
  const [descontoConcedido, setDescontoConcedido] = useState(fidelizacao?.desconto || 0);
  const [beneficioDescricao, setBeneficioDescricao] = useState(fidelizacao?.beneficioDescricao || fidelizacao?.beneficios || '');
  const [observacoesCriar, setObservacoesCriar] = useState(fidelizacao?.observacoes || '');

  // Refidelizar (opcionais)
  const [descontoPercentual, setDescontoPercentual] = useState(0);
  const [descontoValorFixo, setDescontoValorFixo] = useState(0);
  const [mesesComDesconto, setMesesComDesconto] = useState(0);
  const [novoPlanoId, setNovoPlanoId] = useState('');
  const [beneficiosAdicionais, setBeneficiosAdicionais] = useState('');
  const [observacoesRefidelizar, setObservacoesRefidelizar] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fidelizacao) {
      const payload = {
        tipoFidelizacao,
        dataInicio: dataInicio || null,
        mesesFidelidade: Number(mesesFidelidade),
        multaRescisao: Number(multaRescisao) || 0,
        descontoConcedido: descontoConcedido !== '' ? Number(descontoConcedido) : undefined,
        beneficioDescricao: beneficioDescricao || undefined,
        observacoes: observacoesCriar || undefined
      };

      onSubmit(payload);
      return;
    }

    const payload = {
      tipoFidelizacao,
      mesesFidelidade: Number(mesesFidelidade),
      descontoPercentual: descontoPercentual !== '' ? Number(descontoPercentual) : undefined,
      descontoValorFixo: descontoValorFixo !== '' ? Number(descontoValorFixo) : undefined,
      mesesComDesconto: mesesComDesconto !== '' ? Number(mesesComDesconto) : undefined,
      novoPlanoId: novoPlanoId ? Number(novoPlanoId) : undefined,
      beneficiosAdicionais: beneficiosAdicionais || undefined,
      observacoes: observacoesRefidelizar || undefined
    };

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-[90vw] max-h-[90vh] w-[min(90vw,720px)] h-[min(90vw,720px)] mx-4 flex flex-col">
        <h2 className="text-xl font-bold mb-4">
          {fidelizacao ? 'Refidelizar Contrato' : 'Criar Fidelização'}
        </h2>
  <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Fidelização</label>
            <select
              value={tipoFidelizacao}
              onChange={(e) => setTipoFidelizacao(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
              required
            >
              <option value="permanencia">Permanência</option>
              <option value="fidelidade_12_meses">Fidelidade - 12 meses</option>
              <option value="fidelidade_24_meses">Fidelidade - 24 meses</option>
              <option value="fidelidade_36_meses">Fidelidade - 36 meses</option>
              <option value="sem_fidelidade">Sem Fidelidade</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Meses de Fidelidade</label>
            <select
              value={mesesFidelidade}
              onChange={(e) => setMesesFidelidade(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
              required
            >
              <option value={12}>12 meses</option>
              <option value={24}>24 meses</option>
              <option value={36}>36 meses</option>
            </select>
          </div>

          {!fidelizacao ? (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Data de Início</label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Valor da Multa (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={multaRescisao}
                  onChange={(e) => setMultaRescisao(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Desconto Concedido (R$) - opcional</label>
                <input
                  type="number"
                  step="0.01"
                  value={descontoConcedido}
                  onChange={(e) => setDescontoConcedido(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Benefícios (descrição)</label>
                <textarea
                  value={beneficioDescricao}
                  onChange={(e) => setBeneficioDescricao(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Observações (opcional)</label>
                <textarea
                  value={observacoesCriar}
                  onChange={(e) => setObservacoesCriar(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                  rows="2"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Desconto Percentual (%) - opcional</label>
                <input
                  type="number"
                  step="0.01"
                  value={descontoPercentual}
                  onChange={(e) => setDescontoPercentual(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Desconto Valor Fixo (R$) - opcional</label>
                <input
                  type="number"
                  step="0.01"
                  value={descontoValorFixo}
                  onChange={(e) => setDescontoValorFixo(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Meses com Desconto - opcional</label>
                <input
                  type="number"
                  value={mesesComDesconto}
                  onChange={(e) => setMesesComDesconto(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Novo Plano - opcional</label>
                <select
                  value={novoPlanoId}
                  onChange={(e) => setNovoPlanoId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                >
                  <option value="">Manter plano atual</option>
                  {planos.map((pl) => (
                    <option key={pl.id} value={pl.id}>
                      {pl.nomePlano || pl.nomeComercial || `Plano ${pl.id}`} - R$ {pl.valor ?? pl.valorPlanoScm ?? pl.valor}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Benefícios Observações (opcional)</label>
                <textarea
                  value={beneficiosAdicionais}
                  onChange={(e) => setBeneficiosAdicionais(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Observações (opcional)</label>
                <textarea
                  value={observacoesRefidelizar}
                  onChange={(e) => setObservacoesRefidelizar(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
                  rows="2"
                />
              </div>
            </>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {fidelizacao ? 'Refidelizar' : 'Criar Fidelização'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const QuebrarFidelizacaoModal = ({ fidelizacao, onClose, onSubmit }) => {
  const [motivo, setMotivo] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!motivo.trim()) {
      toast.error('Por favor, informe o motivo');
      return;
    }
    onSubmit(motivo);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4 text-red-600">
          ⚠️ Quebrar Fidelização
        </h2>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Atenção:</strong> Ao quebrar a fidelização, uma multa de <strong>R$ {fidelizacao?.multa}</strong> será aplicada.
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
            Meses restantes: {fidelizacao?.mesesRestantes}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Motivo da Quebra *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800"
              rows="4"
              placeholder="Descreva o motivo da quebra de fidelização..."
              required
            />
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Confirmar Quebra
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VincularEquipamentoModal = ({ onClose, onSubmit }) => {
  const [equipamentosDisponiveis, setEquipamentosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    equipamentoId: '',
    tipoVinculo: 'comodato',
    dataInstalacao: format(new Date(), 'yyyy-MM-dd'),
    observacoes: ''
  });

  useEffect(() => {
    fetchEquipamentosDisponiveis();
  }, []);

  const fetchEquipamentosDisponiveis = async () => {
    try {
      const response = await api.get('/equipamentos/disponiveis');
      setEquipamentosDisponiveis(response.data);
    } catch (error) {
      toast.error('Erro ao carregar equipamentos disponíveis');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.equipamentoId) {
      toast.error('Por favor, selecione um equipamento');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Router className="w-6 h-6 text-blue-600" />
          Vincular Equipamento
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Equipamento *
              </label>
              <select
                value={formData.equipamentoId}
                onChange={(e) => setFormData({ ...formData, equipamentoId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Selecione um equipamento...</option>
                {equipamentosDisponiveis.map((equip) => (
                  <option key={equip.id} value={equip.id}>
                    {equip.modelo} - {equip.macAddress} {equip.tipo?.nome ? `(${equip.tipo.nome})` : ''}
                  </option>
                ))}
              </select>
              {equipamentosDisponiveis.length === 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  ⚠️ Nenhum equipamento disponível para vinculação
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Tipo de Vínculo *
              </label>
              <select
                value={formData.tipoVinculo}
                onChange={(e) => setFormData({ ...formData, tipoVinculo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="comodato">Comodato</option>
                <option value="venda">Venda</option>
                <option value="aluguel">Aluguel</option>
                <option value="cortesia">Cortesia</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Data de Instalação
              </label>
              <input
                type="date"
                value={formData.dataInstalacao}
                onChange={(e) => setFormData({ ...formData, dataInstalacao: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Informações adicionais sobre a instalação..."
              />
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={equipamentosDisponiveis.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Vincular Equipamento
              </button>
            </div>
          </form>
          )}
      </div>
    </div>
  );
};

const DesvincularEquipamentoModal = ({ equipamento, onClose, onSubmit }) => {
  const [motivoDesvinculo, setMotivoDesvinculo] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(motivoDesvinculo);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
          <XCircle className="w-6 h-6" />
          Desvincular Equipamento
        </h2>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
            <strong>Equipamento:</strong> {equipamento?.equipamento?.modelo || 'N/A'}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>MAC:</strong> {equipamento?.equipamento?.macAddress || '-'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Motivo do Desvínculo
            </label>
            <textarea
              value={motivoDesvinculo}
              onChange={(e) => setMotivoDesvinculo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-red-500"
              rows="4"
              placeholder="Ex: Troca de equipamento, devolução, defeito..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Este campo é opcional, mas recomendado para histórico
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Confirmar Desvínculo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdicionarServicoModal = ({ servicosDisponiveis, onClose, onSubmit }) => {
  const [servicoSelecionado, setServicoSelecionado] = useState('');
  const [loading, setLoading] = useState(false);

  const servicoDetalhes = servicosDisponiveis.find(s => s.id === Number(servicoSelecionado));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!servicoSelecionado) {
      toast.error('Por favor, selecione um serviço');
      return;
    }
    setLoading(true);
    await onSubmit(servicoSelecionado);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Package className="w-6 h-6 text-purple-600" />
          Adicionar Serviço Adicional
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Selecione o Serviço *
            </label>
            <select
              value={servicoSelecionado}
              onChange={(e) => setServicoSelecionado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Escolha um serviço...</option>
              {servicosDisponiveis.map((servico) => (
                <option key={servico.id} value={servico.id}>
                  {servico.nome} - R$ {parseFloat(servico.valorMensal).toFixed(2)}/mês
                  {servico.categoria ? ` (${servico.categoria})` : ''}
                </option>
              ))}
            </select>
            {servicosDisponiveis.length === 0 && (
              <p className="text-sm text-yellow-600 mt-2">
                ⚠️ Todos os serviços disponíveis já estão vinculados a este contrato
              </p>
            )}
          </div>

          {/* Preview do Serviço Selecionado */}
          {servicoDetalhes && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">
                {servicoDetalhes.nome}
              </h3>
              {servicoDetalhes.descricao && (
                <p className="text-sm text-purple-800 dark:text-purple-200 mb-3">
                  {servicoDetalhes.descricao}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-purple-700 dark:text-purple-300">Valor Mensal:</span>
                  <p className="font-bold text-purple-900 dark:text-purple-100">
                    R$ {parseFloat(servicoDetalhes.valorMensal).toFixed(2)}
                  </p>
                </div>
                {servicoDetalhes.valorInstalacao > 0 && (
                  <div>
                    <span className="text-purple-700 dark:text-purple-300">Taxa de Instalação:</span>
                    <p className="font-bold text-purple-900 dark:text-purple-100">
                      R$ {parseFloat(servicoDetalhes.valorInstalacao).toFixed(2)}
                    </p>
                  </div>
                )}
                {servicoDetalhes.categoria && (
                  <div>
                    <span className="text-purple-700 dark:text-purple-300">Categoria:</span>
                    <p className="font-semibold text-purple-900 dark:text-purple-100">
                      {servicoDetalhes.categoria}
                    </p>
                  </div>
                )}
                {servicoDetalhes.requerEquipamento && (
                  <div className="col-span-2">
                    <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-sm">
                      <Router className="w-4 h-4" />
                      Requer equipamento adicional
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || servicosDisponiveis.length === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Adicionando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Adicionar Serviço
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RemoverServicoModal = ({ servico, onClose, onSubmit }) => {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(motivo);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
          <XCircle className="w-6 h-6" />
          Remover Serviço Adicional
        </h2>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
            <strong>Serviço:</strong> {servico?.nome || 'N/A'}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Valor:</strong> R$ {parseFloat(servico?.valorMensal || 0).toFixed(2)}/mês
          </p>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Atenção:</strong> Ao remover este serviço, o valor da mensalidade do contrato será recalculado.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Motivo da Remoção
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 focus:ring-2 focus:ring-red-500"
              rows="3"
              placeholder="Ex: Cliente não utilizava o serviço, redução de custos..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Este campo é opcional, mas recomendado para histórico
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Removendo...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Confirmar Remoção
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContratoDetalhes;